import {
  Button,
  Card,
  Grid,
  Group,
  NumberInput,
  Select,
  Switch,
  Tabs,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import { IconDeviceFloppy, IconPlus, IconX } from '@tabler/icons-react';
import SearchableCombobox from '../components/SearchableCombobox';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ErrorMessage from '../components/ErrorMessage';
import HeaderStatistic from '../components/HeaderStatistic';
import Metadata from '../components/Metadata';
import MetricsSelector from '../components/MetricsSelector';
import ProductFeedSelector from '../components/ProductFeeds/ProductFeedSelector';
import SearchAsYouTypeBlockedTab from '../components/SearchAsYouType/SearchAsYouTypeBlockedTab';
import SearchAsYouTypeSynonymsTab from '../components/SearchAsYouType/SearchAsYouTypeSynonymsTab';
import Spinner from '../components/Spinner';
import { Page } from '../constants/pageTitles';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../helpers/dateUtils';
import useFetch from '../helpers/useFetch';

const defaultSearch = {
  enabled: true,
  cssSelector: '',
  templateId: null,
  numberOfProductsToShow: 6,
  filters: [],
  blocked: [],
  customSynonymPairs: [],
  synonymPairsFiles: [],
  localeBlocked: false,
  useDescription: true,
  productFeedId: null,
};

const tabs = ['general', 'synonymPairs', 'blockedWords'];

const renderModeOptions = [
  { value: 'dropdown', label: 'Releva dropdown (default)' },
  { value: 'intercept', label: "Intercept the theme's own search" },
];

const responseKindOptions = [
  { value: 'html', label: 'HTML' },
  { value: 'json', label: 'JSON' },
];

const SearchAsYouTypePage = () => {
  const { accountId, domainId } = useParams();
  const domain = useSelector((state) => state.domain.domain);
  const fetch = useFetch();

  const initialPeriodDays = 30;

  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [stats, setStats] = useState();

  const [search, setSearch] = useState();
  const [initialSearch, setInitialSearch] = useState();
  const [templates, setTemplates] = useState();
  const [error, setError] = useState();
  const [customFields, setCustomFields] = useState();
  const [tab, setTab] = useState(tabs[0]);
  const [useProductFeed, setUseProductFeed] = useState(false);
  const [creatingSearch, setCreatingSearch] = useState(false);

  const STATIC_ID = 1;

  const initialSearchStringified = JSON.stringify(initialSearch);
  const searchChanged = initialSearchStringified !== JSON.stringify(search);

  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}`;

  useEffect(() => {
    fetch(`${fetchUrl}/search`)
      .then(({ search }) => {
        setSearch(search);
        setInitialSearch(search);
        setUseProductFeed(!!search?.productFeedId);
      })
      .catch(setError);

    fetch(`${fetchUrl}/templates`)
      .then(({ templates }) => setTemplates(templates))
      .catch(setError);

    fetch(`${fetchUrl}/customFields`)
      .then(({ customFields }) => {
        const productCustomFields = customFields?.product;
        productCustomFields.unshift({ key: 'Categories' });
        setCustomFields(productCustomFields);
      })
      .catch(setError);

    // Convert ISO date strings to full timestamps for the API
    const startTs = encodeURIComponent(dateToStartOfDay(startDate, 'local'));
    const endTs = encodeURIComponent(dateToEndOfDay(endDate, 'local'));
    fetch(`${fetchUrl}/searchAsYouType/stats/${startTs}/${endTs}`)
      .then((stats) => {
        setStats(
          Object.keys(stats).reduce((accu, statKey) => {
            accu[statKey] = { [STATIC_ID]: stats[statKey] };
            return accu;
          }, {})
        );
      })
      .catch(setError);
  }, [fetchUrl, endDate, fetch, startDate]);

  const addSearch = (record) => {
    setCreatingSearch(true);

    fetch(`${fetchUrl}/search`, {
      method: 'POST',
      body: JSON.stringify(record),
    })
      .then(({ search }) => {
        setSearch(search);
        setInitialSearch(search);
      })
      .catch(setError)
      .finally(() => setCreatingSearch(false));
  };

  const updateSearch = (search) => {
    setError(undefined);

    // A term-less pair is only a row waiting to be typed into, and Elasticsearch rejects a synonym
    // rule with no terms, so those rows stay in the form but never reach the API.
    const payload = {
      ...search,
      customSynonymPairs: search.customSynonymPairs?.filter((pair) => pair.value?.length),
    };

    fetch(`${fetchUrl}/search`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
      .then(({ search }) => {
        setSearch(search);
        setInitialSearch(search);
      })
      .catch(setError);
  };

  const handleChange = (e) => {
    setSearch((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // The intercept selectors are nullable server-side, so a blank input means
  // "not set" rather than "match the empty string".
  const handleNullableChange = (name, value) => handleChange({ target: { name, value: value.trim() ? value : null } });

  const handleInterceptChange = (name, value) => {
    setSearch((prev) => {
      const intercept = { ...prev.intercept };
      if (value?.trim()) {
        intercept[name] = value;
      } else {
        delete intercept[name];
      }

      return { ...prev, intercept: Object.keys(intercept).length ? intercept : null };
    });
  };

  // Server-side default, kept out of the record until the operator picks one so
  // that saving an otherwise-untouched search does not start sending a mode.
  const renderMode = search?.renderMode || 'dropdown';

  if (!templates || !customFields) {
    return <Spinner />;
  }

  const templateOptions = [
    { value: '', label: 'Please select a layout' },
    ...templates
      .filter((template) => template.scope === 'search')
      .map((template) => ({ value: template.id.toString(), label: template.name })),
  ];

  const customFieldOptions = [
    { value: '', label: 'Please select a field' },
    ...customFields.map((field) => ({ value: field.key, label: field.key })),
  ];

  return (
    <>
      <Metadata title={Page.searchAsYouType} />

      <div style={{ paddingBottom: 'var(--mantine-spacing-md)' }}>
        <MetricsSelector
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          rowSize="12"
        />
      </div>
      <Card withBorder>
        {stats === undefined ? (
          <Spinner />
        ) : (
          <Grid>
            <Grid.Col span={12}>
              <Grid columns={10}>
                <Grid.Col span={{ base: 10, sm: 2 }}>
                  <HeaderStatistic
                    title="Interactions"
                    stats={stats}
                    stat="autocompleteInteractions"
                    id={STATIC_ID}
                    statKey="count"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 10, sm: 2 }}>
                  <HeaderStatistic
                    title="Product Clicks"
                    stats={stats}
                    stat="autocompleteProductClicks"
                    percentageOfStat="autocompleteInteractions"
                    id={STATIC_ID}
                    statKey="count"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 10, sm: 2 }}>
                  <HeaderStatistic
                    title="Conversions"
                    stats={stats}
                    stat="autocompleteConversions"
                    percentageOfStat="autocompleteProductClicks"
                    id={STATIC_ID}
                    statKey="count"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 10, sm: 2 }}>
                  <HeaderStatistic
                    title="Purchases"
                    stats={stats}
                    stat="autocompletePurchases"
                    percentageOfStat="autocompleteConversions"
                    id={STATIC_ID}
                    statKey="count"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 10, sm: 2 }}>
                  <HeaderStatistic
                    title="Revenue"
                    stats={stats}
                    stat="autocompletePurchases"
                    id={STATIC_ID}
                    statKey="value"
                    currency={domain.currency}
                  />
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        )}
      </Card>

      <Card withBorder mb="sm">
        <Grid mb="md" data-tour="searchAsYouType">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Search As you type</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Group gap="md">
              <Switch
                aria-label="Search As you type"
                checked={search?.enabled ?? false}
                disabled={creatingSearch || search === undefined}
                onChange={(e) => {
                  if (search === null) {
                    addSearch(defaultSearch);
                  } else {
                    handleChange({ target: { name: 'enabled', value: e.currentTarget.checked } });
                  }
                }}
              />
              {search === null && (
                <Button
                  variant="default"
                  size="xs"
                  disabled={creatingSearch}
                  onClick={() => addSearch({ ...defaultSearch, enabled: false })}
                >
                  Set up without turning it on
                </Button>
              )}
            </Group>
            {initialSearch && !initialSearch.enabled && (
              <Text size="xs" c="dimmed" mt="xs">
                Search is not running for shoppers. The settings below can be edited and saved, and take effect once you
                turn it on.
              </Text>
            )}
          </Grid.Col>
        </Grid>
      </Card>

      {search && (
        <Tabs value={tab} onChange={setTab} mb="md">
          <Tabs.List>
            <Tabs.Tab value="general">General</Tabs.Tab>
            <Tabs.Tab value="synonymPairs">Synonym Pairs</Tabs.Tab>
            <Tabs.Tab value="blockedWords">Blocked Words</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general">
            <Card withBorder>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                  <label>CSS Selector for Search Bar</label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TextInput
                    name="cssSelector"
                    autoComplete="nope"
                    value={search.cssSelector || ''}
                    onChange={(e) => handleChange(e)}
                  />
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                  <label>Where to Render Results</label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Select
                    aria-label="Where to Render Results"
                    data={renderModeOptions}
                    allowDeselect={false}
                    value={renderMode}
                    onChange={(value) => handleChange({ target: { name: 'renderMode', value } })}
                  />
                  <Text size="xs" c="dimmed" mt="xs">
                    Intercepting replaces the theme&apos;s own search results with Releva&apos;s, rendered inside the
                    theme&apos;s container.
                  </Text>
                </Grid.Col>
              </Grid>
              {renderMode === 'intercept' && (
                <>
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                      <label>CSS Selector to Render Into</label>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <TextInput
                        aria-label="CSS Selector to Render Into"
                        autoComplete="nope"
                        value={search.mountSelector || ''}
                        onChange={(e) => handleNullableChange('mountSelector', e.target.value)}
                      />
                      <Text size="xs" c="dimmed" mt="xs">
                        The theme container Releva renders the results into.
                      </Text>
                    </Grid.Col>
                  </Grid>
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                      <label>CSS Selector to Hide</label>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <TextInput
                        aria-label="CSS Selector to Hide"
                        autoComplete="nope"
                        value={search.suppressSelector || ''}
                        onChange={(e) => handleNullableChange('suppressSelector', e.target.value)}
                      />
                      <Text size="xs" c="dimmed" mt="xs">
                        The theme&apos;s own results container, hidden so it does not show up next to Releva&apos;s.
                      </Text>
                    </Grid.Col>
                  </Grid>
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                      <label>Requests to Intercept</label>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <TextInput
                        aria-label="Requests to Intercept"
                        autoComplete="nope"
                        value={search.intercept?.match || ''}
                        onChange={(e) => handleInterceptChange('match', e.target.value)}
                      />
                      <Text size="xs" c="dimmed" mt="xs">
                        A whitespace-separated list of substrings, not a single substring: a request is intercepted only
                        when ALL of them appear in its URL. Listing too few makes the connector hijack unrelated theme
                        requests.
                      </Text>
                    </Grid.Col>
                  </Grid>
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                      <label>Search Term URL Parameter</label>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <TextInput
                        aria-label="Search Term URL Parameter"
                        autoComplete="nope"
                        value={search.intercept?.queryParam || ''}
                        onChange={(e) => handleInterceptChange('queryParam', e.target.value)}
                      />
                    </Grid.Col>
                  </Grid>
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                      <label>Response the Theme Expects</label>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <Select
                        aria-label="Response the Theme Expects"
                        data={responseKindOptions}
                        placeholder="Please select a response type"
                        allowDeselect={false}
                        value={search.intercept?.responseKind || null}
                        onChange={(value) => handleInterceptChange('responseKind', value)}
                      />
                    </Grid.Col>
                  </Grid>
                </>
              )}
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                  <label>Layout for Search Results</label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SearchableCombobox
                    name="templateId"
                    options={templateOptions}
                    value={search.templateId?.toString() || ''}
                    onChange={(value) => handleChange({ target: { name: 'templateId', value } })}
                  />
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                  <label>Number of Products to Show</label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <NumberInput
                    name="numberOfProductsToShow"
                    value={search.numberOfProductsToShow || ''}
                    onChange={(value) => handleChange({ target: { name: 'numberOfProductsToShow', value } })}
                  />
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                  <label>Include "Description" Field in Search</label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Switch
                    checked={search.useDescription}
                    onChange={(e) => {
                      handleChange({ target: { name: 'useDescription', value: e.currentTarget.checked } });
                    }}
                  />
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                  <label>Use Product Feed</label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Switch
                    checked={useProductFeed}
                    onChange={(e) => {
                      setUseProductFeed(e.currentTarget.checked);
                      handleChange({ target: { name: 'productFeedId', value: null } });
                    }}
                  />
                </Grid.Col>
              </Grid>
              {(useProductFeed || search.productFeedId) && (
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                    <label>Choose Product Feed</label>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <ProductFeedSelector
                      scope="web"
                      accountId={accountId}
                      domainId={domainId}
                      productFeedId={search.productFeedId}
                      onSelect={(id) => handleChange({ target: { name: 'productFeedId', value: id } })}
                      forType="recommender"
                    />
                  </Grid.Col>
                </Grid>
              )}
              <hr />
              <Grid mb="md">
                <Grid.Col span={12}>
                  <label>Filters to be visible in the search popup:</label>
                </Grid.Col>

                {search.filters &&
                  search.filters.map((filter, index) => (
                    <Grid.Col span={12} key={index}>
                      <Grid>
                        <Grid.Col span={{ base: 12, sm: 5 }}>
                          <Text size="sm" c="dimmed" mb="xs">
                            Title
                          </Text>
                          <TextInput
                            name="filtersName"
                            autoComplete="nope"
                            value={filter.title || ''}
                            onChange={(e) => {
                              const changedFilters = search.filters.map((f, i) =>
                                i === index ? { ...f, title: e.target.value } : f
                              );
                              handleChange({ target: { name: 'filters', value: changedFilters } });
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 5 }}>
                          <Text size="sm" c="dimmed" mb="xs">
                            Custom Field Values for Filter
                          </Text>
                          <SearchableCombobox
                            name="filtersValue"
                            options={customFieldOptions}
                            value={filter.key || ''}
                            onChange={(value) => {
                              const key = value;
                              const type = customFields.find((cf) => cf.key === key)?.type;

                              const changedFilters = search.filters.map((f, i) =>
                                i === index ? { ...f, key, type } : f
                              );
                              handleChange({ target: { name: 'filters', value: changedFilters } });
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 2 }} style={{ textAlign: 'center', alignSelf: 'flex-end' }} mb="xs">
                          <Button
                            fullWidth
                            onClick={() => {
                              const newFilters = search.filters.filter((f, i) => i !== index);
                              handleChange({ target: { name: 'filters', value: newFilters } });
                            }}
                            color="red"
                            size="sm"
                            p="xs"
                          >
                            <IconX size={16} />
                          </Button>
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Text size="sm" c="dimmed" mb="xs">
                            Excluded values
                          </Text>
                          <TagsInput
                            aria-label={`Excluded values for filter ${index + 1}`}
                            clearable
                            data={[]}
                            value={filter.excludedValues || []}
                            onChange={(typedValues) => {
                              // TagsInput commits a pending value untrimmed on blur.
                              const excludedValues = [...new Set(typedValues.map((v) => v.trim()).filter(Boolean))];
                              const changedFilters = search.filters.map((f, i) =>
                                i === index ? { ...f, excludedValues } : f
                              );
                              handleChange({ target: { name: 'filters', value: changedFilters } });
                            }}
                            placeholder="Add a value to hide from this filter..."
                          />
                          <Text size="xs" c="dimmed" mt="xs">
                            Values are matched exactly as they are shown on the chip.
                          </Text>
                        </Grid.Col>
                      </Grid>
                    </Grid.Col>
                  ))}

                <Grid.Col span={12}>
                  <Button
                    fullWidth
                    onClick={() => {
                      const prevFilters = search.filters || [];
                      const e = {
                        target: {
                          name: 'filters',
                          value: [...prevFilters, { title: '', key: '', type: '' }],
                        },
                      };
                      handleChange(e);
                    }}
                    variant="default"
                    size="sm"
                    pt="sm" pb="sm"
                  >
                    <IconPlus size={16} /> Add New Filter
                  </Button>
                </Grid.Col>
              </Grid>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="synonymPairs">
            <SearchAsYouTypeSynonymsTab search={search} handleSearchChange={handleChange} />
          </Tabs.Panel>
          <Tabs.Panel value="blockedWords">
            <SearchAsYouTypeBlockedTab search={search} handleSearchChange={handleChange} domain={domain} />
          </Tabs.Panel>
        </Tabs>
      )}
      <div style={{ textAlign: 'right', marginTop: 'var(--mantine-spacing-md)' }}>
        <Button disabled={!searchChanged} onClick={() => updateSearch(search)}>
          <IconDeviceFloppy size={16} /> {searchChanged ? 'Save' : 'Saved'}
        </Button>
        <ErrorMessage message={error} />
      </div>
    </>
  );
};

export default SearchAsYouTypePage;
